/**
 * Which list controls a resource type may actually offer (GDN-2734).
 *
 * The rule everywhere here: a control is shown only if the running backend
 * honors it. The admin lists previously rendered sort headers and filters the
 * generic endpoint silently dropped, because the table component offered them
 * regardless — so the source of truth is the type's declared capability, not
 * the frontend registration.
 *
 * A registration that outruns the backend degrades to a missing control, never
 * an inert one.
 */

/** Owner dimensions the list route understands directly. */
const PATIENT_KEY = 'patient_id';
const PROVIDER_KEY = 'provider_id';

export interface ListCapability {
  org?: boolean;
  user?: boolean;
  patient?: boolean;
  /** Fields the type declared it can order by. */
  sortable?: string[];
  /** Fields the type declared it can filter on. */
  filterable?: string[];
}

export interface FilterOption {
  key: string;
  label: string;
  type: 'select' | 'text';
  options?: { value: string; label: string }[];
}

/**
 * Filters worth rendering, given what the type declared and where we are.
 *
 * Owner dimensions come from the route rather than the `filterable` set, and
 * the provider filter is dropped at user scope: the URL already pins the
 * owner, so offering a box to type a different one is a contradiction.
 */
export function offeredFilters(
  registered: FilterOption[],
  capability: ListCapability | null | undefined,
  scope: { userId?: string },
): FilterOption[] {
  const declared = capability?.filterable ?? [];
  return registered.filter(filter => {
    if (filter.key === PATIENT_KEY) return capability?.patient ?? false;
    if (filter.key === PROVIDER_KEY) {
      return (capability?.user ?? false) && !scope.userId;
    }
    return declared.includes(filter.key);
  });
}

/** Column keys the backend will actually order by. */
export function sortableColumns(
  capability: ListCapability | null | undefined,
): Set<string> {
  return new Set(capability?.sortable ?? []);
}

/**
 * Sort params to send, or nothing.
 *
 * An undeclared sort is dropped rather than sent: the endpoint answers 400 for
 * one it doesn't recognise, and a stale URL shouldn't turn the list into an
 * error page.
 */
export function sortParams(
  sortBy: string | undefined,
  sortOrder: 'asc' | 'desc' | undefined,
  capability: ListCapability | null | undefined,
): { sortBy: string; sortOrder: 'asc' | 'desc' } | Record<string, never> {
  if (!sortBy || !sortableColumns(capability).has(sortBy)) return {};
  return { sortBy, sortOrder: sortOrder === 'asc' ? 'asc' : 'desc' };
}

/** Declared filter values to forward, dropping blanks and owner dimensions. */
export function filterParams(
  filters: Record<string, string>,
  capability: ListCapability | null | undefined,
): Record<string, string> {
  const declared = capability?.filterable ?? [];
  return Object.fromEntries(
    declared
      .filter(name => filters[name])
      .map(name => [name, filters[name]] as const),
  );
}
